import { SESSION_STORAGE_KEYS } from '@features/auth/constants/sessionStorageKeys';
import { usePendingInvitationsStore } from '@features/user/invitations/store/pendingInvitationsStore';

export type ClearClientSessionArtifactsOptions = {
  /** When true (explicit logout), also remove the post-login return URL. */
  clearReturnUrl: boolean;
};

/**
 * Clears client session caches that should not leak across users on a shared device.
 * Return URL is cleared only on explicit logout so involuntary session expiry can resume.
 */
export function clearClientSessionArtifacts({
  clearReturnUrl,
}: ClearClientSessionArtifactsOptions): void {
  if (clearReturnUrl) {
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.REDIRECT_AFTER_LOGIN);
  }

  sessionStorage.removeItem(SESSION_STORAGE_KEYS.LAST_ENTITY_CONTEXT);
  sessionStorage.removeItem(SESSION_STORAGE_KEYS.HEADER_DISPLAY_NAME);
  sessionStorage.removeItem(SESSION_STORAGE_KEYS.HEADER_DISPLAY_NAME_OWNER);

  const keysToRemove: string[] = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key?.startsWith(SESSION_STORAGE_KEYS.ENTITY_NAME_PREFIX)) {
      keysToRemove.push(key);
    }
  }
  for (const key of keysToRemove) {
    sessionStorage.removeItem(key);
  }

  usePendingInvitationsStore.getState().reset();
}

/**
 * Accepts only same-app relative paths for post-login restore.
 * Rejects protocol-relative (`//…`), absolute URLs, and empty/non-path values.
 */
export function isSafePostLoginRedirect(path: string): boolean {
  if (!path || typeof path !== 'string') return false;
  if (!path.startsWith('/')) return false;
  if (path.startsWith('//')) return false;
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(path)) return false;
  return true;
}
